import React, { useMemo } from 'react';
import { useForm } from '../../hooks/useForm';
import StatusSection from '../StatusSection/StatusSection';
import { Row, StyledFirstColumn, StyledSecondColumn } from '../../PlusWebsiteEditor.styles';
import HeroBlock from '../HeroBlock/HeroBlock';
import PharmacyRequiredSection from '../PharmacyRequiredSection';
import BannersSection from '../BannersSection/BannersSection';
import AboutUsSection from '../AboutUsSection/AboutUsSection';
import ContactUsSection from '../ContactUsSection/ContactUsSection';
import FooterCustomLinks from '../FooterCustomLinks/FooterCustomLinks';
import SeoSection from '../SeoSection/SeoSection';
import LogoSection from '../LogoSection/LogoSection';
import FaviconSection from '../IconSection/IconSection';
import BrandingSection from '../BrandingSection/BrandingSection';
import DomainSection from '../DomainSection/DomainSection';
import HomepageSections from '../HomepageSections/HomepageSections';
import OnboardingSection from '../OnboardingSection/OnboardingSection';
import { WebsiteSection } from '../../types/sections';
import { SectionId } from '../../types/websiteConfiguration';

type Props = {
  form: ReturnType<typeof useForm>;
  availableSections: WebsiteSection[];
  postalPharmaciesCount?: number;
  setNotification: (notification: string) => void;
};

const allCtaLinks = [
  { title: 'Prescriptions', url: '/prescriptions/patients', filter: 'prescription' },
  { title: 'Pharmacy services', url: '/pharmacy_services', filter: 'pharmacy-services' },
  { title: 'Pharmacy shop', url: '/marketplace', filter: 'pharmacy-shop' },
];

const PharmacyLayout = ({ form, availableSections, postalPharmaciesCount, setNotification }: Props) => {
  const {
    values,
    handleChange,
    setFieldValue,
    getError,
    handleBlur,
    heroHandlers,
    faviconHandlers,
    bannersHandlers,
    aboutUsHandlers,
    contactUsHandlers,
    footerCustomLinksHandlers,
    seoHandlers,
    brandingHandlers,
    homepageSectionsHandlers,
  } = form;

  const ctaPossibleLinks = useMemo(() => {
    return allCtaLinks
      .filter((link) => values?.homepage_sections?.length
        ? values.homepage_sections.includes(link.filter as SectionId)
        : true
      )
      .map(({ filter, ...link}) => link);
  }, [values?.homepage_sections]); 

  return (
    <>
      <StatusSection enabled={values.enabled} onSwitch={setFieldValue} deployed={values.deployed} />
      <Row>
        <StyledFirstColumn>
          <HeroBlock
            setNotification={setNotification}
            setFieldValue={setFieldValue}
            ctaPossibleLinks={ctaPossibleLinks}
            {...heroHandlers}
            {...values.hero}
            pharmacy_required={values.pharmacy_required}
            pharmacy_required_redirect={values.pharmacy_required_redirect}
          />
          <PharmacyRequiredSection
            onBlur={handleBlur}
            getError={getError}
            onFieldChange={setFieldValue}
            pharmacy_required={values.pharmacy_required}
            pharmacy_required_redirect={values.pharmacy_required_redirect}
          />
          <BannersSection {...bannersHandlers} banners={values.banners} />
          <AboutUsSection {...aboutUsHandlers} {...values.about_us} />
          <ContactUsSection {...contactUsHandlers} {...values.contact_us} />
          <FooterCustomLinks {...footerCustomLinksHandlers} links={values.footer_custom_links} />
          <SeoSection {...seoHandlers} {...values.meta} />
        </StyledFirstColumn>
        <StyledSecondColumn>
          <LogoSection
            primaryColor={values.branding?.colors?.primary}
            getError={getError}
            onChange={setFieldValue}
            logo={values.logo}
            logo_url={values.logo_url}
            logo_white={values.logo_white}
            logo_url_white={values.logo_url_white}
          />
          <FaviconSection {...faviconHandlers} favicon={values.favicon} />
          <BrandingSection {...brandingHandlers} {...values.branding} />
          <DomainSection
            onBlur={handleBlur}
            getError={getError}
            onFieldChange={setFieldValue}
            onChange={handleChange}
            domain={values.domain}
            homepage_url={values.homepage_url}
          />
          <HomepageSections
            selectedSections={values.homepage_sections}
            availableSections={availableSections}
            {...homepageSectionsHandlers}
          />
          <OnboardingSection
            postalPharmaciesCount={postalPharmaciesCount}
            onChange={handleChange}
            onboardingJourney={values.onboarding_journey}
          />
        </StyledSecondColumn>
      </Row>
    </>
  );
};

export default PharmacyLayout;
